import { Flex } from "@redotech/redo-web/flex";
import { ImageLightbox } from "@redotech/redo-web/image-lightbox";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import type { FormattedChatMessage } from "../../hooks/use-chat-thread-view";

interface ChatMessageBubbleProps {
  message: FormattedChatMessage;
}

export const ChatMessageBubble = memo(function ChatMessageBubble({
  message,
}: ChatMessageBubbleProps) {
  if (message.isFromCustomer) {
    return <CustomerBubble message={message} />;
  }
  return <MerchantBubble message={message} />;
});

const CustomerBubble = memo(function CustomerBubble({
  message,
}: ChatMessageBubbleProps) {
  const hasAttachments = message.attachments.length > 0;
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);

  return (
    <>
      <Flex dir="column" gap="xs" pr="5xl">
        <Text fontSize="xs" textColor="tertiary">
          {message.formattedTime}
        </Text>
        <Flex dir="column" gap="sm" w="fit-content">
          {message.content && (
            <Flex bgColor="tertiary" p="lg" radius="md">
              <Text
                fontSize="sm"
                style={{ wordBreak: "break-word" }}
                textColor="primary"
              >
                {message.content}
              </Text>
            </Flex>
          )}
          {hasAttachments && (
            <Flex dir="column" gap="xs">
              {message.attachments.map((attachment) => {
                const description =
                  attachment.description._tag === "Some"
                    ? attachment.description.value
                    : "Attachment";
                const mimeType =
                  attachment.mimeType._tag === "Some"
                    ? attachment.mimeType.value
                    : null;
                const isImage = mimeType?.startsWith("image/");

                return (
                  <Flex dir="column" gap="xs" key={attachment.id}>
                    {isImage ? (
                      <img
                        alt={description}
                        onClick={() => setLightboxImageUrl(attachment.url)}
                        src={attachment.url}
                        style={{
                          maxWidth: "300px",
                          maxHeight: "300px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      />
                    ) : (
                      <Flex
                        align="center"
                        bgColor="tertiary"
                        gap="sm"
                        p="md"
                        radius="md"
                      >
                        <Text fontSize="sm" textColor="primary">
                          {description}
                        </Text>
                        <a
                          href={attachment.url}
                          rel="noopener noreferrer"
                          style={{ color: "inherit" }}
                          target="_blank"
                        >
                          <Text fontSize="xs" textColor="secondary">
                            Download
                          </Text>
                        </a>
                      </Flex>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Flex>
      <ImageLightbox
        imageSrc={lightboxImageUrl ?? undefined}
        onClose={() => setLightboxImageUrl(null)}
        open={lightboxImageUrl !== null}
      />
    </>
  );
});

const MerchantBubble = memo(function MerchantBubble({
  message,
}: ChatMessageBubbleProps) {
  const hasAttachments = message.attachments.length > 0;
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);

  return (
    <>
      <Flex align="flex-end" dir="column" gap="xs" pl="5xl">
        <Text fontSize="xs" textColor="tertiary">
          {message.formattedTime}
        </Text>
        <Flex dir="column" gap="sm">
          {message.content && (
            <Flex bgColor="alternate" p="lg" radius="md">
              <Text
                fontSize="sm"
                style={{ wordBreak: "break-word" }}
                textColor="white"
              >
                {message.content}
              </Text>
            </Flex>
          )}
          {hasAttachments && (
            <Flex dir="column" gap="xs">
              {message.attachments.map((attachment) => {
                const description =
                  attachment.description._tag === "Some"
                    ? attachment.description.value
                    : "Attachment";
                const mimeType =
                  attachment.mimeType._tag === "Some"
                    ? attachment.mimeType.value
                    : null;
                const isImage = mimeType?.startsWith("image/");

                return (
                  <Flex dir="column" gap="xs" key={attachment.id}>
                    {isImage ? (
                      <img
                        alt={description}
                        onClick={() => setLightboxImageUrl(attachment.url)}
                        src={attachment.url}
                        style={{
                          maxWidth: "300px",
                          maxHeight: "300px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          cursor: "pointer",
                        }}
                      />
                    ) : (
                      <Flex
                        align="center"
                        bgColor="alternate"
                        gap="sm"
                        p="md"
                        radius="md"
                      >
                        <Text fontSize="sm" textColor="white">
                          {description}
                        </Text>
                        <a
                          href={attachment.url}
                          rel="noopener noreferrer"
                          style={{ color: "inherit" }}
                          target="_blank"
                        >
                          <Text fontSize="xs" textColor="secondary">
                            Download
                          </Text>
                        </a>
                      </Flex>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Flex>
      <ImageLightbox
        imageSrc={lightboxImageUrl ?? undefined}
        onClose={() => setLightboxImageUrl(null)}
        open={lightboxImageUrl !== null}
      />
    </>
  );
});
